export interface QuizAnswer {
  text: string
  tag: string
}

export interface QuizQuestion {
  id: number
  phase:
    | 'personalization'
    | 'pain'
    | 'mechanism'
    | 'commitment'
    | 'segmentation'
  phaseLabel: string
  question: string
  isMomentumBooster: boolean
  answers: QuizAnswer[]
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: 1,
    phase: 'personalization',
    phaseLabel: 'Personalización',
    question: '¿Cuál es tu rango de edad?',
    isMomentumBooster: true,
    answers: [
      { text: '18–24', tag: 'AGE_18_24' },
      { text: '25–34', tag: 'AGE_25_34' },
      { text: '35–44', tag: 'AGE_35_44' },
      { text: '45–54', tag: 'AGE_45_54' },
      { text: '55+', tag: 'AGE_55_PLUS' },
    ],
  },
  {
    id: 2,
    phase: 'personalization',
    phaseLabel: 'Personalización',
    question: 'Hoy, ¿cómo describirías tu "momento" personal?',
    isMomentumBooster: false,
    answers: [
      {
        text: 'Siento que "algo" está cambiando en mí, pero no sé qué es',
        tag: 'STAGE_AWAKENING',
      },
      {
        text: 'Estoy despertando, pero me cuesta sostenerlo en lo cotidiano',
        tag: 'STAGE_INTEGRATING',
      },
      {
        text: 'Ya vi demasiadas cosas… y necesito ordenarlas',
        tag: 'STAGE_OVEREXPOSED',
      },
      {
        text: 'Me siento estancado/a y quiero un reinicio',
        tag: 'STAGE_STUCK',
      },
      {
        text: 'Estoy bien, pero sé que hay un siguiente nivel',
        tag: 'STAGE_NEXT_LEVEL',
      },
    ],
  },
  {
    id: 3,
    phase: 'personalization',
    phaseLabel: 'Personalización',
    question: 'Cuando intentas entender tu vida, normalmente tú…',
    isMomentumBooster: false,
    answers: [
      {
        text: 'Analizo todo y termino con más preguntas',
        tag: 'STYLE_OVERTHINKER',
      },
      {
        text: 'Siento cosas fuertes, pero no sé explicarlas',
        tag: 'STYLE_SENSITIVE',
      },
      {
        text: 'Me voy a la acción… aunque por dentro esté temblando',
        tag: 'STYLE_ACTION',
      },
      {
        text: 'Me distraigo (redes/series/trabajo) para no sentir',
        tag: 'STYLE_AVOIDANT',
      },
      {
        text: 'Cambio de método rápido porque me aburro',
        tag: 'STYLE_NOVELTY',
      },
    ],
  },
  {
    id: 4,
    phase: 'pain',
    phaseLabel: 'Tu realidad actual',
    question: '¿Qué está más presente últimamente?',
    isMomentumBooster: false,
    answers: [
      {
        text: 'Ansiedad / presión interna sin razón clara',
        tag: 'PAIN_ANXIETY',
      },
      {
        text: 'Vacío (logro cosas, pero no se siente "real")',
        tag: 'PAIN_EMPTY',
      },
      {
        text: 'Confusión (sé que hay más, pero no lo puedo ver)',
        tag: 'PAIN_CONFUSION',
      },
      { text: 'Cansancio mental / espiritual', tag: 'PAIN_EXHAUSTED' },
      {
        text: 'Miedo a que "algo malo" pase si bajo la guardia',
        tag: 'PAIN_FEAR',
      },
    ],
  },
  {
    id: 5,
    phase: 'pain',
    phaseLabel: 'Tu realidad actual',
    question: 'Si miras tus últimos 12–24 meses, ¿qué patrón se repite más?',
    isMomentumBooster: false,
    answers: [
      {
        text: 'Relaciones: misma dinámica con diferentes personas',
        tag: 'PATTERN_RELATIONSHIPS',
      },
      {
        text: 'Dinero/trabajo: avanzo y luego algo me frena',
        tag: 'PATTERN_MONEY',
      },
      { text: 'Autoestima: dudas / autosabotaje', tag: 'PATTERN_SELF_WORTH' },
      {
        text: 'Salud/energía: subo y bajo, sin estabilidad',
        tag: 'PATTERN_ENERGY',
      },
      {
        text: 'Propósito: no sé hacia dónde va mi vida',
        tag: 'PATTERN_PURPOSE',
      },
    ],
  },
  {
    id: 6,
    phase: 'pain',
    phaseLabel: 'Tu realidad actual',
    question: '¿Qué te dispara más fácil?',
    isMomentumBooster: false,
    answers: [
      {
        text: 'Sentirme ignorado/a o no valorado/a',
        tag: 'TRIGGER_VALIDATION',
      },
      { text: 'Perder control / incertidumbre', tag: 'TRIGGER_CONTROL' },
      {
        text: 'Compararme con otros (y sentirme atrás)',
        tag: 'TRIGGER_COMPARISON',
      },
      {
        text: 'Sentir que "ya debería haberlo resuelto"',
        tag: 'TRIGGER_SHOULD',
      },
      {
        text: 'Sentir que el mundo es injusto / manipulado',
        tag: 'TRIGGER_INJUSTICE',
      },
    ],
  },
  {
    id: 7,
    phase: 'pain',
    phaseLabel: 'Tu realidad actual',
    question: '¿Qué has probado con más intención?',
    isMomentumBooster: false,
    answers: [
      { text: 'Meditación / respiración', tag: 'TRIED_MEDITATION' },
      { text: 'Manifestación / ley de atracción', tag: 'TRIED_MANIFEST' },
      { text: 'Terapia / coaching / somática', tag: 'TRIED_THERAPY' },
      {
        text: 'Psicodélicos o exploración de estados (o curiosidad fuerte)',
        tag: 'TRIED_STATES',
      },
      {
        text: '"De todo un poco", pero nada se queda',
        tag: 'TRIED_EVERYTHING',
      },
    ],
  },
  {
    id: 8,
    phase: 'pain',
    phaseLabel: 'Tu realidad actual',
    question: 'Elige la frase que más te pega hoy:',
    isMomentumBooster: true,
    answers: [
      {
        text: '"Estoy cansado/a de arreglarme sin parar."',
        tag: 'IDENT_SELF_FIX_LOOP',
      },
      {
        text: '"Sé que hay un código… pero no lo veo."',
        tag: 'IDENT_CODE_BLIND',
      },
      {
        text: '"Quiero despertar, pero sin perder mi vida normal."',
        tag: 'IDENT_INTEGRATE_NORMAL',
      },
      {
        text: '"Siento que estoy repitiendo la misma escena."',
        tag: 'IDENT_REPEAT_SCENE',
      },
      { text: '"Estoy listo/a para jugar distinto."', tag: 'IDENT_READY_PLAY' },
    ],
  },
  {
    id: 9,
    phase: 'mechanism',
    phaseLabel: 'Tu visión',
    question: 'En el fondo, ¿qué crees que es la realidad?',
    isMomentumBooster: false,
    answers: [
      {
        text: 'Un sistema con patrones (tipo "código") que se puede aprender',
        tag: 'BELIEF_CODE',
      },
      {
        text: 'Algo espiritual/místico que se siente, más que entenderse',
        tag: 'BELIEF_MYSTIC',
      },
      {
        text: 'Mayormente azar… pero a veces hay señales raras',
        tag: 'BELIEF_RANDOM',
      },
      {
        text: 'Un juego de conciencia donde tu atención crea experiencia',
        tag: 'BELIEF_ATTENTION',
      },
      {
        text: 'No lo sé… pero quiero descubrirlo sin humo',
        tag: 'BELIEF_SKEPTICAL_OPEN',
      },
    ],
  },
  {
    id: 10,
    phase: 'mechanism',
    phaseLabel: 'Tu visión',
    question: '¿Dónde sientes que el "sistema" te atrapa más?',
    isMomentumBooster: false,
    answers: [
      {
        text: 'En mi mente (ruido, dudas, historia interna)',
        tag: 'MATRIX_MIND',
      },
      {
        text: 'En el tiempo (prisa, urgencia, "no me alcanza")',
        tag: 'MATRIX_TIME',
      },
      {
        text: 'En la identidad (roles, expectativas, quedar bien)',
        tag: 'MATRIX_IDENTITY',
      },
      {
        text: 'En el miedo (amenaza constante aunque todo esté "bien")',
        tag: 'MATRIX_FEAR',
      },
      {
        text: 'En la distracción (scroll, estímulo, no puedo parar)',
        tag: 'MATRIX_DISTRACTION',
      },
    ],
  },
  {
    id: 11,
    phase: 'mechanism',
    phaseLabel: 'Tu visión',
    question: 'Si fueras a "despertar más", ¿qué te preocupa?',
    isMomentumBooster: false,
    answers: [
      { text: 'Perderme / volverme extremo/a o raro/a', tag: 'OBJ_LOSE_SELF' },
      { text: 'Ver cosas que me asusten', tag: 'OBJ_AFRAID_SEE' },
      { text: 'Quedar solo/a (que otros no me entiendan)', tag: 'OBJ_ALONE' },
      {
        text: 'Descubrir que he vivido en automático demasiado tiempo',
        tag: 'OBJ_REGRET',
      },
      { text: 'No sostenerlo y volver al mismo loop', tag: 'OBJ_RELAPSE' },
    ],
  },
  {
    id: 12,
    phase: 'commitment',
    phaseLabel: 'Tu siguiente paso',
    question:
      'Si en 7 días tuvieras claridad real, lo primero que cambiaría sería…',
    isMomentumBooster: true,
    answers: [
      { text: 'Mi paz interna (menos ansiedad)', tag: 'GOAL_PEACE' },
      { text: 'Mis decisiones (dejar de dudar)', tag: 'GOAL_DECISIONS' },
      { text: 'Mi energía (levantarme "con señal")', tag: 'GOAL_ENERGY' },
      {
        text: 'Mis relaciones (poner límites / elegir mejor)',
        tag: 'GOAL_RELATIONSHIPS',
      },
      {
        text: 'Mi dirección (saber qué estoy construyendo)',
        tag: 'GOAL_DIRECTION',
      },
    ],
  },
  {
    id: 13,
    phase: 'commitment',
    phaseLabel: 'Tu siguiente paso',
    question:
      'Si te doy un plan simple, ¿cuánto podrías comprometerte por 7 días?',
    isMomentumBooster: false,
    answers: [
      { text: '3–5 minutos/día', tag: 'COMMIT_5' },
      { text: '10 minutos/día', tag: 'COMMIT_10' },
      { text: '20 minutos/día', tag: 'COMMIT_20' },
      { text: '30 minutos/día', tag: 'COMMIT_30' },
      {
        text: 'Quiero el plan, pero necesito que sea ultra simple',
        tag: 'COMMIT_SIMPLE',
      },
    ],
  },
  {
    id: 14,
    phase: 'segmentation',
    phaseLabel: 'Tu código',
    question: 'Cuando la vida se pone intensa, tu reacción automática es…',
    isMomentumBooster: false,
    answers: [
      {
        text: 'Me voy a la mente: analizo, entiendo, pero me quedo trabado/a',
        tag: 'TYPE_OBSERVADOR_ATRAPADO',
      },
      {
        text: 'Busco otro método/explicación (algo "me falta")',
        tag: 'TYPE_BUSCADOR_BUCLE',
      },
      {
        text: 'Intento controlar todo para sentir seguridad',
        tag: 'TYPE_PROTECTOR_CONTROL',
      },
      {
        text: 'Me desconecto / me canso / pierdo la magia',
        tag: 'TYPE_REBELDE_CANSADO',
      },
      {
        text: 'Me culpo y siento que "yo soy el problema"',
        tag: 'TYPE_DYNAMIC',
      },
    ],
  },
]
